import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { 
  LayoutDashboard, 
  TrendingUp, 
  Briefcase, 
  DollarSign, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  FileText,
  MapPin,
  Award,
  Trophy,
  Calendar,
  ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getDistrictByCounty } from "@/utils/txdotDistricts";
import { getLettingStatus, getUpcomingLettings } from "@/utils/txdotLettingDates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders } = useAuth();
  const { division } = useDivision();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [topDistricts, setTopDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('all'); // 'month', 'quarter', 'year', 'all'

  useEffect(() => {
    fetchDashboardMetrics();
  }, [division]);

  // Filter data by time period
  const filterByPeriod = (items, dateField = 'created_at') => {
    if (!items || timePeriod === 'all') return items;
    
    const now = new Date();
    let startDate;
    
    switch (timePeriod) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return items;
    }
    
    return items.filter(item => {
      const itemDate = new Date(item[dateField] || item.created_at || item.letting_date);
      return itemDate >= startDate;
    });
  };

  const fetchDashboardMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, {
        headers: getAuthHeaders(),
        params: { division }
      });
      setMetrics(response.data);
      
      // Calculate top districts if Texas
      if (division === 'texas' && response.data.all_projects_data) {
        calculateTopDistricts(response.data.all_projects_data, response.data.all_bids_data);
      }
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTopDistricts = (projects, bids) => {
    const districtStats = {};
    
    // Calculate from all projects (active, completed, ar, paid)
    projects.forEach(project => {
      const county = project.county;
      const district = getDistrictByCounty(county);
      
      if (district && district !== 'Unknown') {
        if (!districtStats[district]) {
          districtStats[district] = {
            name: district,
            revenue: 0,
            project_count: 0,
            total_margin: 0,
            margin_count: 0
          };
        }
        
        districtStats[district].revenue += project.bid_amount || 0;
        districtStats[district].project_count += 1;
        
        // Add margin if available (from completed projects with operations data)
        if (project.actual_margin !== undefined && project.actual_margin !== null) {
          districtStats[district].total_margin += project.actual_margin;
          districtStats[district].margin_count += 1;
        } else if (project.estimated_margin) {
          districtStats[district].total_margin += project.estimated_margin;
          districtStats[district].margin_count += 1;
        }
      }
    });
    
    // Calculate average margins and sort
    const districtArray = Object.values(districtStats).map(d => ({
      ...d,
      avg_margin: d.margin_count > 0 ? d.total_margin / d.margin_count : 0
    }));
    
    // Sort by revenue (primary), then project count
    districtArray.sort((a, b) => {
      if (b.revenue !== a.revenue) return b.revenue - a.revenue;
      return b.project_count - a.project_count;
    });
    
    setTopDistricts(districtArray.slice(0, 3));
  };

  if (loading) {
    return (
      <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="flex items-center justify-center h-64">
          <div className="text-white text-xl">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Get TXDOT letting status
  const lettingStatus = getLettingStatus();
  const upcomingLettings = getUpcomingLettings(3);

  // Letting banner styles based on urgency
  const getLettingBannerStyles = (type) => {
    switch (type) {
      case 'today':
      case 'active':
        return 'border-red-500 bg-gradient-to-r from-red-900/30 to-red-800/20 animate-pulse';
      case 'tomorrow':
        return 'border-orange-500 bg-gradient-to-r from-orange-900/30 to-orange-800/20';
      case 'soon':
        return 'border-yellow-500 bg-gradient-to-r from-yellow-900/20 to-yellow-800/10';
      case 'upcoming':
        return 'border-blue-500 bg-gradient-to-r from-blue-900/20 to-blue-800/10';
      default:
        return 'border-[#f97316] bg-gradient-to-r from-[#f97316]/10 to-transparent';
    }
  };

  const getLettingIconColor = (type) => {
    switch (type) {
      case 'today':
      case 'active':
        return 'text-red-500';
      case 'tomorrow':
        return 'text-orange-500';
      case 'soon':
        return 'text-yellow-500';
      case 'upcoming':
        return 'text-blue-500';
      default:
        return 'text-[#f97316]';
    }
  };

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-7xl mx-auto">
        {/* TXDOT Letting Banner - Only show for Texas division */}
        {division === 'texas' && lettingStatus && (
          <Card className={`millmetrix-card p-4 mb-6 border-2 ${getLettingBannerStyles(lettingStatus.type)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Calendar className={`w-8 h-8 ${getLettingIconColor(lettingStatus.type)} flex-shrink-0`} />
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    {lettingStatus.message}
                    {(lettingStatus.type === 'today' || lettingStatus.type === 'active') && (
                      <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
                    )}
                  </h2>
                  <p className="text-sm text-[#94a3b8]">{lettingStatus.detail}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Upcoming lettings preview */}
                <div className="hidden md:flex items-center gap-2 text-xs text-[#64748b]">
                  <span>Upcoming:</span>
                  {upcomingLettings.slice(1, 3).map((letting, idx) => (
                    <span key={idx} className="bg-[#1e293b] px-2 py-1 rounded">
                      {letting.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  ))}
                </div>
                <a
                  href="https://www.txdot.gov/business/letting-bids/letting-schedule.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-[#f97316] hover:text-[#fb923c] transition-colors"
                >
                  TXDOT Schedule
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Card>
        )}

        {/* Header with Period Toggle */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <LayoutDashboard className="w-10 h-10 text-[#f97316]" />
              Dashboard
            </h1>
            <p className="text-[#94a3b8]">Your business overview at a glance</p>
          </div>
          
          {/* Time Period Toggle */}
          <div className="flex bg-[#1e293b] rounded-lg p-1">
            {[
              { value: 'month', label: 'This Month' },
              { value: 'quarter', label: 'This Quarter' },
              { value: 'year', label: 'This Year' },
              { value: 'all', label: 'All Time' }
            ].map(period => (
              <button
                key={period.value}
                onClick={() => setTimePeriod(period.value)}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  timePeriod === period.value 
                    ? 'bg-[#f97316] text-white' 
                    : 'text-[#94a3b8] hover:text-white'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {/* CRITICAL ALERT: Overdue AR */}
        {metrics?.overdue_ar && metrics.overdue_ar.length > 0 && (
          <Card className="millmetrix-card p-6 mb-6 border-2 border-red-500 bg-gradient-to-r from-red-900/20 to-red-800/20">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  ⚠️ Overdue Accounts Receivable
                </h2>
                <p className="text-red-200 mb-4">
                  You have {metrics.overdue_ar.length} project{metrics.overdue_ar.length > 1 ? 's' : ''} in Accounts Receivable 
                  for more than 30 days. Total value: ${metrics.overdue_ar_value.toLocaleString()}
                </p>
                <div className="space-y-3">
                  {metrics.overdue_ar.map((project) => (
                    <div 
                      key={project.id} 
                      className="bg-[#1e293b] p-4 rounded-lg border border-red-500/30 hover:border-red-500 transition-colors cursor-pointer"
                      onClick={() => navigate('/accounts-receivable')}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold">{project.project_name}</p>
                          <p className="text-sm text-[#94a3b8]">
                            Control: {project.control_number} | Contractor: {project.contractor_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-400 font-bold text-lg">{project.days_in_ar} days</p>
                          <p className="text-sm text-[#94a3b8]">${project.bid_amount.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/accounts-receivable')}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                >
                  View Accounts Receivable →
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Open Bids */}
          <Card 
            className="millmetrix-card p-6 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/open-bids')}
          >
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-blue-400" />
              <Clock className="w-5 h-5 text-[#64748b]" />
            </div>
            <h3 className="text-sm text-[#94a3b8] uppercase tracking-widest mb-2">Open Bids</h3>
            <p className="text-3xl font-bold text-white mb-1">{metrics?.open_bids_count || 0}</p>
            <p className="text-sm text-[#64748b]">
              ${(metrics?.open_bids_value || 0).toLocaleString()} total value
            </p>
          </Card>

          {/* Active Projects */}
          <Card 
            className="millmetrix-card p-6 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/projects')}
          >
            <div className="flex items-center justify-between mb-4">
              <Briefcase className="w-8 h-8 text-green-400" />
              <TrendingUp className="w-5 h-5 text-[#64748b]" />
            </div>
            <h3 className="text-sm text-[#94a3b8] uppercase tracking-widest mb-2">Active Projects</h3>
            <p className="text-3xl font-bold text-white mb-1">{metrics?.active_projects_count || 0}</p>
            <p className="text-sm text-[#64748b]">
              ${(metrics?.active_projects_value || 0).toLocaleString()} in progress
            </p>
          </Card>

          {/* Accounts Receivable */}
          <Card 
            className="millmetrix-card p-6 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/accounts-receivable')}
          >
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-yellow-400" />
              {metrics?.overdue_ar && metrics.overdue_ar.length > 0 && (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              )}
            </div>
            <h3 className="text-sm text-[#94a3b8] uppercase tracking-widest mb-2">Accounts Receivable</h3>
            <p className="text-3xl font-bold text-white mb-1">{metrics?.ar_count || 0}</p>
            <p className="text-sm text-[#64748b]">
              ${(metrics?.ar_value || 0).toLocaleString()} outstanding
            </p>
            {metrics?.overdue_ar && metrics.overdue_ar.length > 0 && (
              <p className="text-xs text-red-400 mt-1 font-semibold">
                {metrics.overdue_ar.length} overdue
              </p>
            )}
          </Card>

          {/* Win Rate */}
          <Card className="millmetrix-card p-6">
            <div className="flex items-center justify-between mb-4">
              <Target className="w-8 h-8 text-[#f97316]" />
              <CheckCircle className="w-5 h-5 text-[#64748b]" />
            </div>
            <h3 className="text-sm text-[#94a3b8] uppercase tracking-widest mb-2">Win Rate</h3>
            <p className="text-3xl font-bold text-white mb-1">{metrics?.win_rate || 0}%</p>
            <p className="text-sm text-[#64748b]">
              {metrics?.won_bids_count || 0} of {metrics?.total_bids || 0} bids won
            </p>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Completed Projects */}
          <Card className="millmetrix-card p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Completed Projects
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8]">Total Completed</span>
                <span className="text-white font-semibold">{metrics?.completed_projects_count || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8]">Total Value</span>
                <span className="text-white font-semibold">
                  ${(metrics?.completed_projects_value || 0).toLocaleString()}
                </span>
              </div>
              <button
                onClick={() => navigate('/completed')}
                className="w-full mt-2 px-4 py-2 bg-[#1e293b] hover:bg-[#f97316] text-[#94a3b8] hover:text-white rounded-lg transition-colors"
              >
                View Completed Projects →
              </button>
            </div>
          </Card>

          {/* AR Summary */}
          <Card className="millmetrix-card p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-yellow-400" />
              AR Aging Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8]">0-30 Days</span>
                <span className="text-white font-semibold">{metrics?.ar_0_30_days || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8]">31-60 Days</span>
                <span className="text-yellow-400 font-semibold">{metrics?.ar_31_60_days || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#94a3b8]">Over 60 Days</span>
                <span className="text-red-400 font-semibold">{metrics?.ar_over_60_days || 0}</span>
              </div>
              <button
                onClick={() => navigate('/accounts-receivable')}
                className="w-full mt-2 px-4 py-2 bg-[#1e293b] hover:bg-[#f97316] text-[#94a3b8] hover:text-white rounded-lg transition-colors"
              >
                Manage AR →
              </button>
            </div>
          </Card>
        </div>

        {/* Leaderboards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Top Districts Leaderboard (Texas Only) */}
          {division === 'texas' && topDistricts.length > 0 && (
            <Card className="millmetrix-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#f97316]" />
                Top 3 Districts
              </h3>
              <p className="text-xs text-[#94a3b8] mb-4 uppercase tracking-widest">
                Focus Areas for Expansion
              </p>
              <div className="space-y-4">
                {topDistricts.map((district, index) => (
                  <div 
                    key={district.name} 
                    className="bg-[#1e293b] p-4 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          'bg-amber-700 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-white font-bold">{district.name}</p>
                          <p className="text-xs text-[#94a3b8]">{district.project_count} projects</p>
                        </div>
                      </div>
                      <MapPin className="w-4 h-4 text-[#f97316]" />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-[#94a3b8] uppercase tracking-widest">Revenue</p>
                        <p className="text-lg font-bold text-[#10b981]">
                          ${district.revenue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94a3b8] uppercase tracking-widest">Avg Margin</p>
                        <p className="text-lg font-bold text-[#0ea5e9]">
                          {district.avg_margin.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Top Contractors Leaderboard */}
          {metrics?.top_contractors && metrics.top_contractors.length > 0 && (
            <Card className="millmetrix-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#10b981]" />
                Top 3 Contractors
              </h3>
              <p className="text-xs text-[#94a3b8] mb-4 uppercase tracking-widest">
                Highest Revenue
              </p>
              <div className="space-y-4">
                {metrics.top_contractors.map((contractor, index) => (
                  <div 
                    key={contractor.name} 
                    className="bg-[#1e293b] p-4 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#10b981] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          'bg-amber-700 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-bold">{contractor.name}</p>
                          <p className="text-xs text-[#94a3b8]">{contractor.project_count} projects</p>
                        </div>
                      </div>
                      <CheckCircle className="w-4 h-4 text-[#10b981]" />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-[#94a3b8] uppercase tracking-widest">Total Value</p>
                        <p className="text-lg font-bold text-[#10b981]">
                          ${contractor.total_value.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#94a3b8] uppercase tracking-widest">Avg Project</p>
                        <p className="text-lg font-bold text-[#0ea5e9]">
                          ${contractor.avg_bid.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
